import { Injectable, NgModule } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Resolve,
  RouterModule,
  Routes,
} from "@angular/router";
import { take } from "rxjs/operators";
import { LoggedInGuard } from "../auth/auth.guards";
import { FacilitiesListComponent } from "./facilities-list/facilities-list.component";
import { FacilityService } from "../facilities/facility.service";
import { SessionService } from "../session.service";
import { FacilityComponent } from "./facility/facility.component";
import { BeaconService } from "../beacons/beacon.service";
import { PermissionService } from "../permission.service";

@Injectable({
  providedIn: "root",
})
export class FacilitiesListGuard implements CanActivate {
  constructor(private PermissionService: PermissionService) {}

  canActivate() {
    return this.PermissionService.categories.facilities.hasList();
  }
}

@Injectable({
  providedIn: "root",
})
export class FacilitiesWriteGuard implements CanActivate {
  constructor(private PermissionService: PermissionService) {}

  canActivate() {
    return this.PermissionService.categories.facilities.hasWrite();
  }
}

@Injectable({
  providedIn: "root",
})
export class FacilitiesDetailGuard implements CanActivate {
  constructor(private PermissionService: PermissionService) {}

  canActivate() {
    return this.PermissionService.categories.facilities.hasDetail();
  }
}

@Injectable({ providedIn: "root" })
class FacilitiesListPermissionResolve implements Resolve<any> {
  constructor(private PermissionService: PermissionService) {}
  resolve() {
    return Promise.all([
      this.PermissionService.categories.facilities.hasWrite(),
      this.PermissionService.categories.facilities.hasDetail(),
    ]).then(([hasFacilitiesWrite, hasFacilitiesDetail]) => {
      return {
        hasFacilitiesWrite,
        hasFacilitiesDetail,
      };
    });
  }
}

@Injectable({ providedIn: "root" })
export class FacilityResolve implements Resolve<any> {
  constructor(
    private FacilityService: FacilityService,
    private SessionService: SessionService,
  ) {}
  resolve(route: ActivatedRouteSnapshot) {
    return this.FacilityService.getFacility(
      this.SessionService.currentAccountValue!.accountID,
      route.params.facilityID,
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class FacilitiesResolve implements Resolve<any> {
  constructor(
    private FacilityService: FacilityService,
    private SessionService: SessionService,
  ) {}
  resolve() {
    return this.FacilityService.getFacilities(
      this.SessionService.currentAccountValue!.accountID,
      {
        page: 0,
        perPage: 999999999,
      },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class UnassignedBeaconsResolve implements Resolve<any> {
  constructor(
    private BeaconService: BeaconService,
    private SessionService: SessionService,
  ) {}
  resolve() {
    return this.BeaconService.getBeacons(
      this.SessionService.currentAccountValue!.accountID,
      {
        facility_floor_id: null,
        page: 0,
        perPage: 999999999,
      },
    ).pipe(take(1));
  }
}

const routes: Routes = [
  {
    path: "",
    canActivate: [LoggedInGuard],
    runGuardsAndResolvers: "always",
    data: { sidebar: "infrastructure" },
    children: [
      {
        path: "list",
        component: FacilitiesListComponent,
        resolve: {
          // facilities: FacilitiesResolve,
          permissions: FacilitiesListPermissionResolve,
        },
        data: { title: "Facilities" },
        canActivate: [FacilitiesListGuard],
      },
      {
        path: "create",
        component: FacilityComponent,
        data: { title: "Create New Facility" },
        canActivate: [FacilitiesWriteGuard],
      },
      {
        path: ":facilityID",
        resolve: {
          facility: FacilityResolve,
          unassignedBeacons: UnassignedBeaconsResolve,
        },
        canActivate: [FacilitiesWriteGuard, FacilitiesDetailGuard],
        runGuardsAndResolvers: "paramsChange",
        children: [
          {
            path: "",
            component: FacilityComponent,
            data: { title: "Facility" },
          },
        ],
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class FacilitiesRoutingModule {}

const facilitiesModuleLazyLoadRoutes: Routes = [
  {
    path: "facilities",
    loadChildren: () =>
      import("./facilities.module").then((m) => m.FacilitiesModule),
  },
];

export { facilitiesModuleLazyLoadRoutes };
